// Grab the first decodable frame of a video file and return it as a small
// JPEG for use as a thumbnail. Runs entirely in the browser — no transcoding,
// no server round-trip. Falls back to a plain solid-color blob if the browser
// can't decode the video (rare but possible with iPhone HEVC on old Android).

const MAX_DIM = 480;

export async function makeVideoPoster(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.src = url;
  try {
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
      };
      const onReady = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); reject(new Error('video decode failed')); };
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });
      // Seek slightly past 0 so we don't grab a black-lead frame.
      video.currentTime = 0.1;
    });
    const w = video.videoWidth || 320;
    const h = video.videoHeight || 240;
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.drawImage(video, 0, 0, tw, th);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('poster encode failed'))),
        'image/jpeg',
        0.8
      )
    );
  } catch {
    // Fallback: 24×24 clay-tan placeholder so the tile still shows something.
    return await fallbackPoster();
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function fallbackPoster(): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = 24;
  canvas.height = 24;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Blob([new Uint8Array()], { type: 'image/jpeg' });
  ctx.fillStyle = '#c58a5a';
  ctx.fillRect(0, 0, 24, 24);
  return new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b ?? new Blob([new Uint8Array()], { type: 'image/jpeg' })), 'image/jpeg', 0.7)
  );
}
